import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { clinicAppointmentsAPI } from "../../services/api";
import { pdfServices } from "../../services/pdfservices";
import "../../styles/client-details.css";

/**
 * Client Details (tab under Visit Reports): every pet guardian with
 * appointments or visits at this clinic — show-up record (completed /
 * cancelled / no-show), visit counts, and lifetime money.
 *
 * Rows expand in place (accordion style): clicking a row slides the
 * client's full appointment + visit history open under it — no modal, no
 * navigation. History loads lazily on first open and is cached per client.
 */
const ClientDetails = ({ clinicData }) => {
  const { t, i18n } = useTranslation();
  const clinicId = clinicData?.clinic?.id;

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [perPage] = useState(15);
  const [printing, setPrinting] = useState(false);
  const [showBirthdays, setShowBirthdays] = useState(false);

  const [expandedId, setExpandedId] = useState(null);
  const [expandedDetail, setExpandedDetail] = useState(null); // { id, data }
  const [detailLoading, setDetailLoading] = useState(false);
  const [expandAnim, setExpandAnim] = useState(false);
  const [openSections, setOpenSections] = useState({ appointments: false, visits: false });

  const searchTimeoutRef = useRef(null);

  const currency = clinicData?.clinic?.currency || "JOD";

  // Debounced mirror of the search box: the list refetches 300ms after the
  // user stops typing instead of on every keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    searchTimeoutRef.current = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(searchTimeoutRef.current);
  }, [search]);

  const formatMoney = (value) =>
    Number(value || 0).toLocaleString(i18n.language === "ar" ? "ar-SA" : "en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleDateString(i18n.language === "ar" ? "ar-SA" : "en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const birthdayInfo = (client) => {
    if (client.birthday_in_days === null || !client.date_of_birth) {
      return { date: null, days: null };
    }
    const date = new Date(client.date_of_birth).toLocaleDateString(
      i18n.language === "ar" ? "ar-SA" : "en-US",
      { month: "short", day: "numeric" }
    );
    return { date, days: client.birthday_in_days };
  };

  const statusLabel = (status) => {    const key = `clientDetails.status.${(status || "").toLowerCase()}`;
    const fallback = {
      completed: "Completed",
      checkout: "Checked Out",
      checkedout: "Checked Out",
      active: "Active",
      cancelled: "Cancelled",
      canceled: "Cancelled",
      no_show: "No-Show",
      pending: "Pending",
      confirmed: "Confirmed",
    }[(status || "").toLowerCase()];
    return t(key, fallback || status || "-");
  };

  const fetchClients = useCallback(async () => {
    if (!clinicId) return;
    try {
      setLoading(true);
      setError(null);
      const response = await clinicAppointmentsAPI.getClientDetails({
        clinic_id: clinicId,
        search: debouncedSearch || undefined,
        per_page: perPage,
        page: currentPage,
      });
      setClients(response.data.data?.items || []);
      setTotalPages(response.data.data?.pagination?.last_page || 1);
      // Any list change (search / page) collapses the open row so the user
      // never lands on a detail that belongs to a previous view.
      setExpandedId(null);
      setExpandedDetail(null);
    } catch (err) {
      console.error("Error fetching client details:", err);
      setError(t("clientDetails.error", "Failed to load client details"));
    } finally {
      setLoading(false);
    }
  }, [clinicId, debouncedSearch, currentPage, perPage, t]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  // Slide-down animation: mount the panel collapsed, then flip to open on
  // the next frame so the grid-rows transition actually runs.
  useEffect(() => {
    if (! expandedId) {
      setExpandAnim(false);
      return;
    }
    const frame = requestAnimationFrame(() => setExpandAnim(true));
    return () => cancelAnimationFrame(frame);
  }, [expandedId]);

  const handleSearchChange = (value) => {
    setSearch(value);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setSearch("");
    setCurrentPage(1);
    setShowBirthdays(false);
  };

  const downloadBlob = (blob, defaultFileName) => {
    const disposition = blob.headers?.["content-disposition"];
    let fileName = defaultFileName;
    if (disposition) {
      const match = disposition.match(/filename="?([^"]+)"?/);
      if (match) {
        fileName = match[1];
      }
    }
    const blobUrl = URL.createObjectURL(blob.data);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  };

  const handleClientPrint = async (client) => {
    if (printing || !clinicId) return;
    setPrinting(true);
    try {
      const response = await pdfServices.generateClientDetails({
        clinic_id: clinicId,
        client_id: client.id,
      });
      downloadBlob(response, `client-details-${client.id}.pdf`);
    } catch (err) {
      console.error("Error generating client details PDF:", err);
      alert(t("clientDetails.printError", "Failed to generate the client details PDF"));
    } finally {
      setPrinting(false);
    }
  };

  const handleRowClick = async (client) => {
    if (expandedId === client.id) {
      setExpandedId(null);
      return;
    }

    setExpandedId(client.id);
    setExpandAnim(false);
    setOpenSections({ appointments: false, visits: false });

    // Serve from cache when reopening the same client this session.
    if (expandedDetail?.id === client.id) {
      return;
    }

    setExpandedDetail(null);
    setDetailLoading(true);
    try {
      const response = await clinicAppointmentsAPI.getClientDetail(client.id, {
        clinic_id: clinicId,
      });
      setExpandedDetail({ id: client.id, data: response.data.data });
    } catch (err) {
      console.error("Error fetching client history:", err);
      alert(t("clientDetails.detailError", "Failed to load client history"));
      setExpandedId(null);
    } finally {
      setDetailLoading(false);
    }
  };

  if (loading && clients.length === 0) {
    return (
      <div className="client-details-container">
        <div className="client-details-header">
          <h1 className="client-details-title">
            {t("clientDetails.title", "Client Details")}
          </h1>
        </div>
        <div className="client-details-loading">
          {t("clientDetails.loading", "Loading clients...")}
        </div>
      </div>
    );
  }

  const detail = expandedDetail?.id === expandedId ? expandedDetail.data : null;

  return (
    <div className="client-details-container">
      <div className="client-details-header">
        <h1 className="client-details-title">
          {t("clientDetails.title", "Client Details")}
        </h1>
        <p className="client-details-subtitle">
          {t("clientDetails.subtitle", "Pet guardians who booked or visited your clinic — click a row to see their history")}
        </p>
      </div>

      <div className="client-details-filters">
        <div className="client-details-filter-group">
          <label className="client-details-filter-label">
            {t("clientDetails.filters.search", "Search")}
          </label>
          <input
            type="text"
            className="client-details-filter-input"
            placeholder={t("clientDetails.filters.searchPlaceholder", "Search by guardian name, phone or email...")}
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
        </div>
        <div className="client-details-filter-btns">
          <button
            type="button"
            onClick={() => setShowBirthdays((s) => !s)}
            className={`client-details-toggle-btn ${showBirthdays ? "active" : ""}`}
            title={t("clientDetails.birthdaysToggle", "Show birthdays and upcoming-birthday alerts")}
            aria-pressed={showBirthdays}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 21h16" />
              <path d="M5 21v-8h14v8" />
              <path d="M12 13v8" />
              <path d="M12 13c-1.5 0-3-1.2-3-2.8C9 8.6 12 5 12 5s3 3.6 3 5.2c0 1.6-1.5 2.8-3 2.8z" />
            </svg>
            {t("clientDetails.birthdays", "Birthdays")}
          </button>
          <button onClick={handleClearFilters} className="client-details-clear-btn">
            {t("clientDetails.filters.clear", "Clear Filters")}
          </button>
        </div>
      </div>

      {error && <div className="client-details-empty">{error}</div>}

      {!error && clients.length === 0 && !loading ? (
        <div className="client-details-empty">
          <p>{t("clientDetails.noClients", "No clients found")}</p>
        </div>
      ) : !error ? (
        <>
          <div className="client-details-table-wrapper">
            <table className={`client-details-table ${showBirthdays ? "has-birthday" : ""}`}>
              <thead>
                <tr>
                  <th>{t("clientDetails.table.guardian", "Pet Guardian")}</th>
                  <th>{t("clientDetails.table.phone", "Phone")}</th>
                  <th>{t("clientDetails.table.pets", "Pets")}</th>
                  {showBirthdays && (
                    <th>{t("clientDetails.table.birthday", "Birthday")}</th>
                  )}
                  {showBirthdays && (
                    <th>{t("clientDetails.table.daysUntil", "In")}</th>
                  )}
                  <th className="numeric">{t("clientDetails.table.appointments", "Appts")}</th>
                  <th className="numeric">{t("clientDetails.table.completed", "Completed")}</th>
                  <th className="numeric">{t("clientDetails.table.cancelled", "Cancelled")}</th>
                  <th className="numeric">{t("clientDetails.table.noShow", "No-Show")}</th>
                  <th className="numeric">{t("clientDetails.table.visits", "Visits")}</th>
                  <th className="numeric">{t("clientDetails.table.paid", "Paid")}</th>
                  <th className="numeric">{t("clientDetails.table.balance", "Balance")}</th>
                  <th aria-label={t("clientDetails.expand", "Expand")} />
                </tr>
              </thead>
              <tbody>
                {clients.map((client) => {
                  const isExpanded = expandedId === client.id;
                  const bday = showBirthdays ? birthdayInfo(client) : null;
                  return (
                    <React.Fragment key={client.id}>
                      <tr
                        className={isExpanded ? "is-expanded" : ""}
                        onClick={() => handleRowClick(client)}
                      >
                        <td>{client.name}</td>
                        <td>{client.phone || "-"}</td>
                        <td>
                          {client.pets?.length ? (
                            <span className="client-details-pets">
                              {client.pets.slice(0, 2).join(", ")}
                              {client.pets.length > 2 && (
                                <span className="client-details-pets-more">
                                  {t("clientDetails.morePets", "+{{n}} more", { n: client.pets.length - 2 })}
                                </span>
                              )}
                            </span>
                          ) : (
                            "-"
                          )}
                        </td>
                        {showBirthdays && (
                          <td>
                            {bday.date ? (
                              <span className="client-details-birthday">
                                {bday.date}
                                {bday.days === 0 && (
                                  <span className="client-details-bday-pill is-today">
                                    🎂 {t("clientDetails.birthday.today", "Today")}
                                  </span>
                                )}
                                {bday.days === 1 && (
                                  <span className="client-details-bday-pill is-soon">
                                    {t("clientDetails.birthday.tomorrow", "Tomorrow")}
                                  </span>
                                )}
                                {bday.days !== null && bday.days > 1 && bday.days <= 7 && (
                                  <span className="client-details-bday-pill is-soon">
                                    {t("clientDetails.birthday.inDays", "in {{d}} days", { d: bday.days })}
                                  </span>
                                )}
                              </span>
                            ) : (
                              "-"
                            )}
                          </td>
                        )}
                        {showBirthdays && (
                          <td className={bday.days !== null && bday.days <= 7 ? "client-details-days-soon" : ""}>
                            {bday.days === null ? "-" : bday.days === 0
                              ? t("clientDetails.birthday.today", "Today")
                              : `${bday.days}d`}
                          </td>
                        )}
                        <td className="numeric">{client.appointments_total}</td>
                        <td className="numeric">{client.appointments_completed}</td>
                        <td className={`numeric ${client.appointments_cancelled > 0 ? "client-details-count-warn" : ""}`}>
                          {client.appointments_cancelled}
                        </td>
                        <td className={`numeric ${client.appointments_no_show > 0 ? "client-details-count-bad" : ""}`}>
                          {client.appointments_no_show}
                        </td>
                        <td className="numeric">{client.visits_total}</td>
                        <td className="numeric">{formatMoney(client.paid)} {currency}</td>
                        <td className={`numeric ${client.balance > 0 ? "client-details-balance-due" : ""}`}>
                          {formatMoney(client.balance)} {currency}
                        </td>
                        <td className="client-details-chevron-cell">
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className={`client-details-chevron ${isExpanded ? "open" : ""}`}
                          >
                            <polyline points="6 9 12 15 18 9" />
                          </svg>
                        </td>
                      </tr>

                      {isExpanded && (
                        <tr className="client-details-expand-row">
                          <td colSpan={showBirthdays ? 13 : 11}>
                            <div className={`client-details-expander ${expandAnim ? "open" : ""}`}>
                              <div className="client-details-expander-inner">
                                {detailLoading || !detail ? (
                                  <div className="client-details-history-loading">
                                    {t("clientDetails.loadingHistory", "Loading history...")}
                                  </div>
                                ) : (
                                  <>
                                    <div className="client-details-client-meta">
                                      <div className="client-details-client-meta-info">
                                        {detail.client.phone && <span>{detail.client.phone}</span>}
                                        {detail.client.email && <span>{detail.client.email}</span>}
                                        {detail.history.pets?.length > 0 && (
                                          <span>
                                            {t("clientDetails.pets", "Pets")}: {detail.history.pets.map((p) => p.name).join(", ")}
                                          </span>
                                        )}
                                      </div>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleClientPrint(detail.client);
                                        }}
                                        disabled={printing}
                                        className="client-details-inline-print-btn"
                                        title={t("clientDetails.printClient", "Print this client as a PDF")}
                                      >
                                        {printing ? (
                                          <span className="client-details-print-spinner" />
                                        ) : (
                                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <polyline points="6 9 6 2 18 2 18 9" />
                                            <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                                            <rect x="6" y="14" width="12" height="8" />
                                          </svg>
                                        )}
                                        {t("clientDetails.print", "Print")}
                                      </button>
                                    </div>

                                    <div className="client-details-stats">
                                      <div className="client-details-stat-card">
                                        <div className="client-details-stat-label">
                                          {t("clientDetails.stats.appointments", "Appointments")}
                                        </div>
                                        <div className="client-details-stat-value">{detail.client.appointments_total}</div>
                                      </div>
                                      <div className="client-details-stat-card">
                                        <div className="client-details-stat-label">
                                          {t("clientDetails.stats.completed", "Completed")}
                                        </div>
                                        <div className="client-details-stat-value">{detail.client.appointments_completed}</div>
                                      </div>
                                      <div className="client-details-stat-card">
                                        <div className="client-details-stat-label">
                                          {t("clientDetails.stats.cancelled", "Cancelled")}
                                        </div>
                                        <div className={`client-details-stat-value ${detail.client.appointments_cancelled > 0 ? "is-warn" : ""}`}>
                                          {detail.client.appointments_cancelled}
                                        </div>
                                      </div>
                                      <div className="client-details-stat-card">
                                        <div className="client-details-stat-label">
                                          {t("clientDetails.stats.noShow", "No-Show")}
                                        </div>
                                        <div className={`client-details-stat-value ${detail.client.appointments_no_show > 0 ? "is-warn" : ""}`}>
                                          {detail.client.appointments_no_show}
                                        </div>
                                      </div>
                                      <div className="client-details-stat-card">
                                        <div className="client-details-stat-label">
                                          {t("clientDetails.stats.visits", "Visits")}
                                        </div>
                                        <div className="client-details-stat-value">{detail.client.visits_total}</div>
                                      </div>
                                      <div className="client-details-stat-card">
                                        <div className="client-details-stat-label">
                                          {t("clientDetails.stats.paid", "Paid")}
                                        </div>
                                        <div className="client-details-stat-value">
                                          {formatMoney(detail.client.paid)} {currency}
                                        </div>
                                      </div>
                                      <div className="client-details-stat-card">
                                        <div className="client-details-stat-label">
                                          {t("clientDetails.stats.balance", "Balance")}
                                        </div>
                                        <div className={`client-details-stat-value ${detail.client.balance > 0 ? "is-due" : ""}`}>
                                          {formatMoney(detail.client.balance)} {currency}
                                        </div>
                                      </div>
                                    </div>

                                    <button
                                      type="button"
                                      className="client-details-section-toggle"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setOpenSections((s) => ({ ...s, appointments: !s.appointments }));
                                      }}
                                    >
                                      <span>
                                        {t("clientDetails.sections.appointments", "Appointments")}
                                        {detail.history.appointments.length > 0 && (
                                          <span className="client-details-section-count">
                                            {detail.history.appointments.length}
                                          </span>
                                        )}
                                      </span>
                                      <svg
                                        width="15"
                                        height="15"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className={`client-details-chevron ${openSections.appointments ? "open" : ""}`}
                                      >
                                        <polyline points="6 9 12 15 18 9" />
                                      </svg>
                                    </button>
                                    {openSections.appointments && (
                                      <div className="client-details-section-panel">
                                        {detail.history.appointments.length === 0 ? (
                                          <p className="client-details-expand-empty">
                                            {t("clientDetails.noAppointments", "No appointments recorded.")}
                                          </p>
                                        ) : (
                                          <table className="client-details-history-table">
                                            <thead>
                                              <tr>
                                                <th>{t("clientDetails.table.date", "Date")}</th>
                                                <th>{t("clientDetails.table.pet", "Pet")}</th>
                                                <th>{t("clientDetails.table.reason", "Reason")}</th>
                                                <th>{t("clientDetails.table.status", "Status")}</th>
                                                <th>{t("clientDetails.table.notes", "Notes")}</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {detail.history.appointments.map((appt) => (
                                                <tr key={`appt-${appt.id}`}>
                                                  <td>{formatDate(appt.date)}</td>
                                                  <td>{appt.pet}</td>
                                                  <td>{appt.reason || "-"}</td>
                                                  <td>
                                                    <span className={`client-details-status-badge client-details-status-${(appt.status || "").toLowerCase()}`}>
                                                      {statusLabel(appt.status)}
                                                    </span>
                                                  </td>
                                                  <td>{appt.cancel_reason || "-"}</td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        )}
                                      </div>
                                    )}

                                    <button
                                      type="button"
                                      className="client-details-section-toggle"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setOpenSections((s) => ({ ...s, visits: !s.visits }));
                                      }}
                                    >
                                      <span>
                                        {t("clientDetails.sections.visits", "Visits")}
                                        {detail.history.visits.length > 0 && (
                                          <span className="client-details-section-count">
                                            {detail.history.visits.length}
                                          </span>
                                        )}
                                      </span>
                                      <svg
                                        width="15"
                                        height="15"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className={`client-details-chevron ${openSections.visits ? "open" : ""}`}
                                      >
                                        <polyline points="6 9 12 15 18 9" />
                                      </svg>
                                    </button>
                                    {openSections.visits && (
                                      <div className="client-details-section-panel">
                                        {detail.history.visits.length === 0 ? (
                                          <p className="client-details-expand-empty">
                                            {t("clientDetails.noVisits", "No visits recorded.")}
                                          </p>
                                        ) : (
                                          <table className="client-details-history-table">
                                            <thead>
                                              <tr>
                                                <th>{t("clientDetails.table.date", "Date")}</th>
                                                <th>{t("clientDetails.table.visitNumber", "Visit Number")}</th>
                                                <th>{t("clientDetails.table.type", "Type")}</th>
                                                <th>{t("clientDetails.table.pet", "Pet")}</th>
                                                <th>{t("clientDetails.table.reason", "Reason")}</th>
                                                <th>{t("clientDetails.table.status", "Status")}</th>
                                                <th className="numeric">{t("clientDetails.table.total", "Total")}</th>
                                                <th className="numeric">{t("clientDetails.table.paid", "Paid")}</th>
                                                <th className="numeric">{t("clientDetails.table.balance", "Balance")}</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {detail.history.visits.map((visit) => (
                                                <tr key={`${visit.type}-${visit.id}`}>
                                                  <td>{formatDate(visit.date)}</td>
                                                  <td>{visit.serial}</td>
                                                  <td>{visit.type === "inpatient"
                                                    ? t("clientDetails.inpatient", "Inpatient")
                                                    : t("clientDetails.outpatient", "Outpatient")}</td>
                                                  <td>{visit.pet}</td>
                                                  <td>{visit.reason || "-"}</td>
                                                  <td>
                                                    <span className={`client-details-status-badge client-details-status-${(visit.status || "").toLowerCase()}`}>
                                                      {statusLabel(visit.status)}
                                                    </span>
                                                  </td>
                                                  <td className="numeric">{formatMoney(visit.total)} {currency}</td>
                                                  <td className="numeric">{formatMoney(visit.paid)} {currency}</td>
                                                  <td className={`numeric ${visit.remaining > 0 ? "client-details-balance-due" : ""}`}>
                                                    {formatMoney(visit.remaining)} {currency}
                                                  </td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        )}
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="client-details-pagination">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="client-details-pagination-btn"
              >
                {t("clientDetails.pagination.previous", "Previous")}
              </button>
              <span className="client-details-pagination-info">
                {t("clientDetails.pagination.page", "Page")} {currentPage} {t("clientDetails.pagination.of", "of")} {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="client-details-pagination-btn"
              >
                {t("clientDetails.pagination.next", "Next")}
              </button>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
};

export default ClientDetails;
